import { Modal } from 'antd'
import React, { useState } from 'react'
import { MalindexConfig } from '../../../api/malindex/malindexApi'
import { MODAL_BREDD, MODAL_GRIND, MODAL_KNAPP, modalTitel } from '../modalStil'

/**
 * UppstartModal – tas alltid innan ett besök börjar.
 *
 * Uppstarten låg tidigare som första steget i flödet, vilket gjorde den till en av
 * många skärmar. Som modal blir det tydligt att det är en grind.
 *
 * Två steg, ordnade efter vem frågan riktar sig till:
 *   1  Samtalet   – kanal och vem som lämnar svaren. Allt sker med brukaren.
 *   2  Bakgrund   – demensdiagnos. Handläggarens egen notering, ställs inte till brukaren.
 *
 * INGEN SAMTYCKESFRÅGA. Den fanns tidigare som en hård grind med bara en Ja-knapp,
 * och togs bort 2026-09-14. Samtycke är sällan rätt rättslig grund för en kommun –
 * brukaren är beroende av kommunen och kan inte säga nej fritt – och dokumentation i
 * socialtjänsten är en skyldighet, inte något brukaren beviljar. Frågan antydde alltså
 * ett val som inte fanns, och grinden gjorde att den som sa nej inte fick någon uppföljning.
 *
 * Det som ersätter den är INFORMATION: samtalsbeskrivningen överst, som säger vad
 * samtalet handlar om. Den följer besökstypen, eftersom en kartläggning och en
 * uppföljning handlar om olika saker.
 *
 * ÄrendeID hör INTE hemma här. Det är det enda administrativa i sammanhanget och
 * något handläggaren sällan har i huvudet på plats – det efterfrågas i stället vid
 * sparandet, där det också går att skjuta upp.
 *
 * Går inte att stänga med Esc eller klick utanför – enda vägarna ut är att slutföra
 * eller att avbryta besöket.
 */

export interface UppstartVarden {
  /** Hembesök eller telefonsamtal – metodmässigt olika, måste kunna följas upp. */
  kanal: string | null
  svarande: string | null
  demensdiagnos: boolean | null
}

export interface UppstartModalProps {
  config: MalindexConfig
  open: boolean
  /** Vilket besök som ska startas. Nyckel, inte visningstext – styr även
   *  vilken samtalsbeskrivning som visas. */
  besokstyp: 'kartlaggning' | 'uppfoljning'
  /** Visas i modalen som bekräftelse på vem besöket gäller. */
  personnummer: string
  /**
   * Sant när ett pausat besök återupptas. Då ställs bara steg 1 – hur samtalet
   * genomförs och vem som svarar. Båda kan ha ändrats sedan sist: dagen är en annan,
   * kanske sitter en anhörig med som inte var där då.
   *
   * Demensfrågan ställs INTE om. Den är bakgrundsdata om personen, inte om samtalet,
   * och skulle vi längre fram förenkla formuläret vid demensdiagnos får svaret inte
   * kunna byta mitt i ett besök.
   */
  aterupptar?: boolean
  /** Demenssvaret från när besöket startade. Följer med orört vid återupptagande. */
  tidigareDemensdiagnos?: boolean | null
  onKlar: (varden: UppstartVarden) => void
  onAvbryt: () => void
}

const BESOKSTYP_ORD: Record<string, string> = {
  kartlaggning: 'kartläggning',
  uppfoljning: 'uppföljning',
}

/** Fallback när configen är seedad innan kanalfrågan fanns. */
const KANAL_FRAGA = 'Hur genomförs samtalet?'
const KANAL_ALTERNATIV = [
  { nyckel: 'hembesok', etikett: 'Hembesök' },
  { nyckel: 'telefon', etikett: 'Telefonsamtal' },
]

const valKlass = (aktiv: boolean) =>
  `rounded-sm border px-4 py-2.5 text-base transition-colors ${
    aktiv
      ? 'border-pejlen-500 bg-pejlen-500 text-white font-medium'
      : 'border-gray-200 bg-white text-gray-700 hover:border-pejlen-400 hover:text-pejlen-500'
  }`

const JaNej: React.FC<{ varde: boolean | null; onValj: (v: boolean) => void }> = ({ varde, onValj }) => (
  <div className="flex gap-2">
    <button type="button" aria-pressed={varde === true} onClick={() => onValj(true)} className={valKlass(varde === true)}>
      Ja
    </button>
    <button
      type="button"
      aria-pressed={varde === false}
      onClick={() => onValj(false)}
      className={valKlass(varde === false)}
    >
      Nej
    </button>
  </div>
)

const UppstartModal: React.FC<UppstartModalProps> = ({
  config,
  open,
  besokstyp,
  personnummer,
  aterupptar,
  tidigareDemensdiagnos,
  onKlar,
  onAvbryt,
}) => {
  const [steg, setSteg] = useState(0)
  const [varden, setVarden] = useState<UppstartVarden>({
    kanal: null,
    svarande: null,
    demensdiagnos: aterupptar ? tidigareDemensdiagnos ?? null : null,
  })

  const satt = (delta: Partial<UppstartVarden>) => setVarden((v) => ({ ...v, ...delta }))

  // Vid återupptagande finns bara samtalsfrågorna – demenssteget hoppas över.
  const totaltAntalSteg = aterupptar ? 1 : 2
  const sistaSteget = steg === totaltAntalSteg - 1
  // Steg 1 kräver att det står klart hur samtalet genomförs och vem som svarar – båda
  // styr hur resten av besöket ska läsas. Demensdiagnosen blockerar inte, den är
  // bakgrundsdata och inte ett villkor.
  const kanGaVidare = steg === 0 ? varden.kanal !== null && varden.svarande !== null : true

  const kanalFraga = config.uppstart.kanalFraga ?? KANAL_FRAGA
  const kanalAlternativ = config.uppstart.kanalAlternativ ?? KANAL_ALTERNATIV

  const samtalsbeskrivning = config.uppstart.samtalsbeskrivning?.[besokstyp]

  return (
    <Modal
      open={open}
      {...MODAL_GRIND}
      title={modalTitel(
        aterupptar
          ? `Innan ni fortsätter — ${BESOKSTYP_ORD[besokstyp] ?? besokstyp}`
          : `Innan besöket börjar — ${BESOKSTYP_ORD[besokstyp] ?? besokstyp}`
      )}
      okText={sistaSteget ? (aterupptar ? 'Fortsätt besöket' : 'Starta besök') : 'Nästa'}
      okButtonProps={{ ...MODAL_KNAPP, disabled: !kanGaVidare }}
      onOk={() => (sistaSteget ? onKlar(varden) : setSteg((s) => s + 1))}
      cancelText={steg === 0 ? 'Avbryt besök' : 'Tillbaka'}
      cancelButtonProps={MODAL_KNAPP}
      // MODAL_GRIND stänger av Esc, mask och kryss, så onCancel kommer bara från knappen.
      onCancel={() => (steg === 0 ? onAvbryt() : setSteg((s) => s - 1))}
      width={MODAL_BREDD}
    >
      {/* Personnumret bekräftas vid grinden i stället för att ligga kvar som chrome
          under samtalet. Fel brukare är det enda oåterkalleliga felet i flödet. */}
      <div className="mb-4 flex items-baseline justify-between">
        <span className="text-base font-medium text-gray-800">{personnummer}</span>
        {/* Ingen stegräknare när det bara finns ett steg – "steg 1 av 1" säger inget. */}
        {totaltAntalSteg > 1 && (
          <span className="text-sm uppercase tracking-wide text-gray-400">
            Steg {steg + 1} av {totaltAntalSteg}
          </span>
        )}
      </div>

      {steg === 0 && (
        <div className="flex flex-col gap-5">
          {/* Informationen till brukaren, först av allt. Den låg tidigare inne i
              samtyckesblocket och visades först när svaranden var vald – men den beror
              bara på besökstypen, och den ska sägas innan frågorna börjar. */}
          {samtalsbeskrivning && <p className="m-0 text-base text-gray-700">{samtalsbeskrivning}</p>}

          <div className="flex flex-col gap-2">
            <span className="text-base font-medium text-gray-800">{kanalFraga}</span>
            <div className="flex flex-wrap gap-2">
              {kanalAlternativ.map((alt) => (
                <button
                  key={alt.nyckel}
                  type="button"
                  aria-pressed={varden.kanal === alt.nyckel}
                  onClick={() => satt({ kanal: alt.nyckel })}
                  className={valKlass(varden.kanal === alt.nyckel)}
                >
                  {alt.etikett}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-base font-medium text-gray-800">{config.uppstart.svarandeFraga}</span>
            <div className="flex flex-wrap gap-2">
              {config.uppstart.svarandeAlternativ.map((alt) => (
                <button
                  key={alt.nyckel}
                  type="button"
                  aria-pressed={varden.svarande === alt.nyckel}
                  onClick={() => satt({ svarande: alt.nyckel })}
                  className={valKlass(varden.svarande === alt.nyckel)}
                >
                  {alt.etikett}
                </button>
              ))}
            </div>
          </div>

        </div>
      )}

      {steg === 1 && (
        <div className="flex flex-col gap-2">
          <span className="text-base font-medium text-gray-800">{config.uppstart.demensFraga}</span>
          <JaNej varde={varden.demensdiagnos} onValj={(v) => satt({ demensdiagnos: v })} />
          {config.uppstart.demensNotis && <span className="text-sm text-gray-400">{config.uppstart.demensNotis}</span>}
        </div>
      )}
    </Modal>
  )
}

export default UppstartModal
